import React, { useContext, useEffect, useRef } from 'react';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import Text from '../../../../shared/components/Text/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import hexToRgba from '../../../../shared/utils/hexToRgba';
import decodeHtmlEntities from '../../../../shared/utils/decodeHtmlEntities';
import { normalizeSupersetColor } from '../../../../shared/constants';
import MonthEditPopoverContext from './MonthEditPopoverContext';
import { store } from '../../../../redux_setup/store';
import {
  setSelectedItems,
  setMonthSelectionActiveDate,
} from '../../ducks/calendarActions';
import { getSessionBreakChunk } from '../../hooks/dragSessionBreakHelpers';

const Container = styled('div')`
  position: relative;
  background-color: ${(props) => {
    /** Month view multi-select highlight (no checkbox here). */
    if (props.isMonthSelected) return 'rgba(119, 167, 255, 0.16)';
    if (props.isActive) {
      return props.hasSessionBreakBackground
        ? 'rgba(0, 0, 0, 0.06)'
        : 'rgba(0, 0, 0, 0.03)';
    }
    if (props.hasSessionBreakBackground) return '#F8F8F8';
    return 'transparent';
  }};
  padding-top: 5px;
  transition: background-color 0.15s ease;
`;

const ItemRow = styled('div')`
  display: flex;
  position: relative;
  padding-bottom: 5px;
  align-items: center;
  margin-left: ${(props) => (props.isWeekView ? '8px' : '0px')};
  margin-right: ${(props) => (props.isWeekView ? '8px' : '0px')};
`;

const LineContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-left: 5px;
  width: 100%;
  overflow-x: hidden;
`;

const Line = styled('div')`
  position: absolute;
  /* When the row above belongs to the same superset, extend the line
   * UP 5px so it reaches this Container's outer top edge — bridging
   * the 5px padding-top gap with the previous item's line. */
  top: ${(props) => (props.doesGroupingLetterAboveItemMatchContent && !props.isDragging && !props.isHovered ? '-5px' : '0px')};
  bottom: ${(props) => (props.doesGroupingLetterBelowItemMatchContent && !props.isDragging && !props.isHovered ? '0' : '8px')};
  width: 2px;
  background-color: ${(props) => props.color || '#E74C3C'};
`;

const TextColumn = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
  overflow: hidden;
  margin-left: 7px;
  position: relative;
`;

const SubTitle = styled('div')`
  overflow: hidden;
  font-size: 12px;
  color: #9E9E9E;
  font-family: 'Nunito Sans';
`;

const IconContainer = styled('div')`
  position: relative;
  background: ${(props) => props.background};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #EEEEEE;
  min-height: 25px;
  min-width: 25px;
  max-width: 25px;
  max-height: 25px;
  border: ${(props) => `1px solid ${props.borderColor}`};
`;

const ParentOrSwapIcon = styled('div')`
  position: absolute;
  top: -10px;
  left: -7px;
  height: 16px;
  width: 16px;
  background: #B5B5B5;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const ParentIconBorderBox = styled('div')`
  border: 1px solid white;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px;
  width: 12px;
  text-align: center;
`;

const IconAndCheckBoxContainer = styled('div')`
display: flex;
height: 100%;
align-self: self-start;
gap: ${(props) => (props.isWeekView ? '0px' : '8px')};
margin-left: 2px;
.checkbox-input {
    visibility: ${(props) => !props.checkboxVisible && 'hidden'};
    appearance: none; /* Remove default checkbox styling */
    min-width: 18px;
    min-height: 18px;
    max-width: 18px;
    max-height: 18px;
    border: 2px solid ${(props) => (props.isChecked ? '#77A7FF' : '#E0E0E0')};
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    position: relative;
  }

  .checkbox-input:checked::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 5px;
    width: 5px;
    height: 10px;
    border: solid ${(props) => (props.isChecked ? '#77A7FF' : '#E0E0E0')};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: block;
  }
`;

const RightBoxPlaceholder = styled('div')`
  display: flex;
  visibility: hidden;
`;

const ClickContainer = styled('div')`
  padding-bottom: ${(props) => props.addPaddingBottom && '5px'};
  display: flex;
  overflow: hidden;
  transition: height 0.3s ease;
  justify-content: space-between;
  align-items: center;
  .rc-menu {
    padding: 10px;
  }
  padding-top: ${(props) => props.isSuperSet && '5px'};
  padding-bottom: ${(props) => (props.type === 'progression' || props.type === 'P') && props.isExpanded && '5px'}
`;

const ReadOnlyExerciseItem = ({
  content,
  exercises = [],
  index,
  date,
  hasSessionBreakBackground = false,
}) => {
  const exercisePickerTextRef = useRef(null);
  const itemRowRef = useRef(null);
  const textWrapperRef = useRef(null);
  const clickContainerRef = useRef(null);
  const containerRef = useRef(null);
  const {
    openPopover, activeAssignedId, isMonthView,
  } = useContext(MonthEditPopoverContext);
  const dispatch = useDispatch();
  const isActive = activeAssignedId === content.assignedId;
  const dragIsInProgress = useSelector(
    (state) => state.calendar.data.dragIsInProgress,
  );
  const selectedItem = useSelector((state) => (
    state.calendar.data.selectedItems[content.assignedId]
  ));

  /** Track when a drag just ended so we can suppress the synthetic
   * click rbd occasionally lets through on same-Droppable reorders.
   * Without this the dropped row's click would open the popover at
   * the end of the drag. Mirrors the pattern the removed
   * MonthDayContent.handleDayClick used to use. */
  const dragWasInProgress = useRef(false);
  const dragEndedAt = useRef(0);
  useEffect(() => {
    if (dragWasInProgress.current && !dragIsInProgress) {
      dragEndedAt.current = Date.now();
    }
    dragWasInProgress.current = dragIsInProgress;
  }, [dragIsInProgress]);

  /** Open the month-view edit popover anchored to this row. No-op when
   * not inside a MonthView (context's default openPopover is a noop),
   * during a drag, or within 250ms after a drag ended. */
  const handleRowClick = (e) => {
    if (dragIsInProgress) return;
    if (Date.now() - dragEndedAt.current < 250) return;

    /** Month view only: Shift+Click builds a day-scoped multi-selection
     * instead of opening the editor. Additive toggle, and shift-clicking
     * into a different day restarts the chain in that day. A session break
     * carries the items beneath it (up to the next break) so the group
     * stays whole when dragged/copied/deleted. */
    if (isMonthView && e?.shiftKey && content.assignedId !== undefined) {
      e.preventDefault();
      const dateKey = date.format('YYYY-MM-DD');
      const { monthSelectionActiveDate, selectedItems } = store.getState().calendar.data;
      const chunk = content.type === 'session_break'
        ? getSessionBreakChunk(exercises, index)
        : [content];

      /** Shift-clicking in a different day restarts the chain there. */
      if (monthSelectionActiveDate && monthSelectionActiveDate !== dateKey) {
        const fresh = {};
        chunk.forEach((it) => { fresh[it.assignedId] = it; });
        dispatch(setSelectedItems(fresh));
        dispatch(setMonthSelectionActiveDate(dateKey));
        return;
      }

      const next = { ...selectedItems };
      const alreadySelected = chunk.some((it) => next[it.assignedId]);
      if (alreadySelected) {
        chunk.forEach((it) => { delete next[it.assignedId]; });
      } else {
        chunk.forEach((it) => { next[it.assignedId] = it; });
      }
      dispatch(setSelectedItems(next));
      /** This day stays active even after deselecting the last item. */
      dispatch(setMonthSelectionActiveDate(dateKey));
      return;
    }

    if (!openPopover) return;
    /** A plain click in month view makes this the active day and resets
     * any existing multi-selection before opening the editor. */
    if (isMonthView) {
      const { selectedItems } = store.getState().calendar.data;
      if (Object.keys(selectedItems || {}).length) dispatch(setSelectedItems({}));
      dispatch(setMonthSelectionActiveDate(date.format('YYYY-MM-DD')));
    }
    openPopover({
      content,
      index,
      date,
      anchorEl: containerRef.current,
    });
  };

  const doesGroupingLetterAboveItemMatchContent = !!content.groupingLetter
    && index > 0
    && content.groupingLetter === exercises[index - 1]?.groupingLetter;

  const doesGroupingLetterBelowItemMatchContent = !!content.groupingLetter
    && index < exercises.length - 1
    && content.groupingLetter === exercises[index + 1]?.groupingLetter;

  const isChecked = !!selectedItem;

  const iconColorWriter = (icon) => {
    if (icon === 'question-mark-circle') {
      return '#9B59B6';
    }
    if (icon === 'science') {
      return '#9B59B6';
    }
    if (icon === 'warmup') {
      return '#E74C3C';
    }
    if (icon === 'lift') {
      return '#6C7A89';
    }
    if (icon === 'cardio') {
      return '#59ABE3';
    }
    if (icon === 'circuit') {
      return '#F89406';
    }
    if (icon === 'cooldown') {
      return '#1787FB';
    }
    if (icon === 'health' || icon === 'heart') {
      return '#E74C3C';
    }
    if (icon === 'note') {
      return '#F9BF3B';
    }
    if (icon === 'chart') {
      return '#FF00BF';
    }
    if (icon === 'tag2' || icon === 'tag') {
      return '#FF4500';
    }
    if (icon === 'page-break' || icon === 'break') {
      return 'white';
    }
    if (icon === 'rest' || icon === 'clock') {
      return '#6C7A89';
    }
    if (icon === 'video-add' || icon === 'video') {
      return '#6C7A89';
    }
    if (icon === 'plus') {
      return '#9E9E9E';
    }
    return '';
  };

  const iconBackgroundColor = ({ line }) => {
    const icon = content.groupingLetter ? 'groupingLetter' : content.icon;
    if (icon === 'question-mark-circle') {
      return 'rgba(155, 89, 182, 0.2)';
    }
    if (icon === 'science') {
      return 'rgba(155, 89, 182, 0.2)';
    }
    if (icon === 'warmup') {
      return 'rgba(231, 76, 60, 0.2)';
    }
    if (icon === 'lift') {
      return 'rgba(108, 122, 137, 0.2)';
    }
    if (icon === 'cardio') {
      return 'rgba(89, 171, 227, 0.2)';
    }
    if (icon === 'circuit') {
      return 'rgba(248, 148, 6, 0.2)';
    }
    if (icon === 'cooldown') {
      return 'rgba(23, 135, 251, 0.2)';
    }
    if (icon === 'health' || icon === 'heart') {
      return 'rgba(231, 76, 60, 0.2)';
    }
    if (icon === 'note') {
      return 'rgba(249, 191, 59, 0.2)';
    }
    if (icon === 'chart') {
      return 'rgba(255, 0, 191, 0.2)';
    }
    if (icon === 'tag2' || icon === 'tag') {
      return 'rgba(255, 69, 0, 0.2)';
    }
    if (icon === 'page-break' || icon === 'break') {
      if (line) {
        return 'rgba(108, 122, 137, 0.2)';
      }
      return '#34495e';
    }
    if (icon === 'rest' || icon === 'clock') {
      return 'rgba(108, 122, 137, 0.2)';
    }
    if (icon === 'video-add' || icon === 'video') {
      return 'rgba(108, 122, 137, 0.2)';
    }
    if (icon === 'plus') {
      return 'white';
    }
    if (icon === 'groupingLetter') {
      return hexToRgba(normalizeSupersetColor(content?.groupingColorCode), 0.2);
    }
    return '';
  };

  const accessoryItem = (icon) => {
    if (icon === 'rest' || icon === 'clock') {
      return true;
    }
    if (icon === 'video-add' || icon === 'video') {
      return true;
    }
    if (icon === 'plus') {
      return true;
    }
    return false;
  };

  /** Count items that fall under this session break (up to the next
   * session break or the end of the day). Mirrors the
   * `(N)` badge that day/week view appends to the session-break title. */
  const numberOfItemsUnderSessionBreak = (() => {
    if (content.type !== 'session_break') return 0;
    let count = 0;
    for (let i = index + 1; i < exercises.length; i += 1) {
      if (exercises[i]?.type === 'session_break') break;
      count += 1;
    }
    return count;
  })();

  const getName = () => {
    if (content.type === 'video' && !content.title) {
      return 'Video';
    }
    if (content.type === 'session_break') {
      const baseTitle = (!content.title || content.title === 'Session')
        ? 'Session Break'
        : content.title;
      return numberOfItemsUnderSessionBreak > 0
        ? `${baseTitle} (${numberOfItemsUnderSessionBreak})`
        : baseTitle;
    }
    if (content.type === 'rest') {
      if (content.restTime) {
        return `Rest Time (${content?.restTime} sec)`;
      }
      return 'Rest Time';
    }
    return content.title;
  };

  return (
    <Container
      ref={containerRef}
      data-assigned-id={content.assignedId}
      hasSessionBreakBackground={hasSessionBreakBackground}
      isActive={isActive}
      isMonthSelected={isMonthView && isChecked}
      onClick={handleRowClick}
    >
      <ItemRow
        ref={itemRowRef}
        style={{ opacity: 1 }}
        doesGroupingLetterAboveItemMatchContent={doesGroupingLetterAboveItemMatchContent}
        doesGroupingLetterBelowItemMatchContent={doesGroupingLetterBelowItemMatchContent}
      >
        <IconAndCheckBoxContainer
          isChecked={isChecked}
        >
          <IconContainer background={accessoryItem(content.icon) && !content.groupingLetter ? 'white' : iconBackgroundColor({ line: false })} borderColor={iconBackgroundColor({ line: false })}>
            {content.groupingLetter ? (
              <Text style={{ margin: 0 }} fontSize='12pt' color={normalizeSupersetColor(content.groupingColorCode)}>{content.groupingLetter}</Text>
            ) : (
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon={content.icon === 'break' ? 'page-break' : content.icon}
                color={iconColorWriter(content.icon)}
              />
            )}
            {content.isInherited && (
            <ParentOrSwapIcon>
              <ParentIconBorderBox>
                <Text fontSize={10} style={{ marginTop: 3 }} color='white'>
                  P
                </Text>
              </ParentIconBorderBox>
            </ParentOrSwapIcon>
            )}
            {content?.swap && (
            <ParentOrSwapIcon>
              <IcomoonReact
                iconSet={iconSet}
                size={10}
                icon='swap'
                color='white'
              />
            </ParentOrSwapIcon>
            )}
          </IconContainer>
        </IconAndCheckBoxContainer>
        <LineContainer>
          <Line
            color={
            content.groupingColorCode
              ? normalizeSupersetColor(content.groupingColorCode)
              : iconBackgroundColor({ line: true })
            }
            doesGroupingLetterAboveItemMatchContent={doesGroupingLetterAboveItemMatchContent}
            doesGroupingLetterBelowItemMatchContent={doesGroupingLetterBelowItemMatchContent}
            isDragging={false}
            isHovered={false}
          />
          <ClickContainer
            isPreSave={content.preSave}
            ref={clickContainerRef}
            type={content.type}
          >
            <TextColumn ref={textWrapperRef}>
              {/* Invisible measurement span for exercise picker text */}
              <span
                ref={exercisePickerTextRef}
                style={{
                  whiteSpace: 'nowrap',
                  font: 'inherit', // IMPORTANT: match font exactly
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  fontSize: 13,
                }}
              >
                {getName()}
              </span>
            </TextColumn>
            <RightBoxPlaceholder />
          </ClickContainer>
          <SubTitle
            style={{
              marginLeft: 7,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginRight: 18,
              maxWidth: 300,
            }}
          >
            {content.additionalInfo && (content.type === 'lift' || content.type === 'L' || content.type === 'T' || content.type === 'tag') ? `${decodeHtmlEntities(content.subTitle)} - ${decodeHtmlEntities(content.additionalInfo)}` : decodeHtmlEntities(content.subTitle) }
          </SubTitle>
        </LineContainer>
      </ItemRow>
    </Container>
  );
};

ReadOnlyExerciseItem.propTypes = {
  content: PropTypes.instanceOf(Object).isRequired,
  exercises: PropTypes.instanceOf(Array),
  index: PropTypes.number.isRequired,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hasSessionBreakBackground: PropTypes.bool,
};

ReadOnlyExerciseItem.defaultProps = {
  exercises: [],
  date: null,
  hasSessionBreakBackground: false,
};

export default ReadOnlyExerciseItem;
